import React, { useCallback, useEffect, useState } from 'react'
import { Loader } from '@refera/ui-web'

import useStyles from './styles'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { Button, FormHelperText, Grid } from '@mui/material'
import { getAgencyAdditionalConfigSelector } from '_/modules/agency/selectors'
import { emailSelector } from '_/modules/email/selectors'
import HeaderTitle from '_/components/header-title'
import { navigate, useParams } from '@reach/router'
import { updateAgencyAdditionalConfig } from '_/modules/agency/actions'
import { Parcelamento } from './parcelamento'
import { decamelize, decamelizeKeys } from 'humps'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

function appendFormData(formData, key, value) {
  if (value && typeof value === 'object' && !(value instanceof Date) && !(value instanceof File)) {
    formData.append(key, JSON.stringify(decamelizeKeys(value)))
  } else {
    formData.append(key, value)
  }
}

export function createFormData(payload) {
  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    appendFormData(formData, decamelize(key), value)
  })

  return formData
}

const schema = yup.object({
  agencyInterestRates: yup
    .array()
    .of(
      yup.object({
        installment: yup
          .number()
          .typeError('Número de parcelamento deve ser um número inteiro')
          .integer('Número de parcelamento deve ser um número inteiro')
          .min(2, 'Número de parcelamento mínimo de 2')
          .max(24, 'Número de parcelamento máximo de 24')
          .required('Número de parcelamento é obrigatório'),
        interestRate: yup
          .number()
          .typeError('Porcentagem deve ser um número')
          .min(0, 'Porcentagem mínimo de 0.00')
          .max(100, 'Porcentagem máximo de 100.00')
          .test('max-decimals', 'Porcentagem deve ter no máximo 4 casas decimais', value =>
            /^\d+(\.\d{1,4})?$/.test(String(value))
          )
          .required('Taxa de juros é obrigatória'),
      })
    )
    .transform((value, original) => (original === '' ? null : value))
    .optional()
    .nullable()
    .test('unique-installment', '', function uniqueInstallmentTest(list) {
      if (!list) return true
      const { path } = this
      const values = list.map(item => item.installment)
      const duplicates = values.filter((v, i, a) => v != null && a.indexOf(v) !== i)
      if (duplicates.length === 0) return true

      const errors = []
      const uniqueDups = Array.from(new Set(duplicates))
      uniqueDups.forEach(dup => {
        list.forEach((item, idx) => {
          if (item.installment === dup) {
            errors.push(
              new yup.ValidationError(
                `Número de parcelamento ${dup} está duplicado`,
                null,
                `${path}[${idx}].installment`
              )
            )
          }
        })
      })
      throw new yup.ValidationError(errors)
    }),
})

export default function AdditionalConfigForm({ getDataForm }) {
  const { agencyId } = useParams()
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const agencyAdditionalConfig = useSelector(getAgencyAdditionalConfigSelector)
  const email = useSelector(emailSelector)
  const [loading, setLoading] = useState(false)

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      agencyInterestRates: [],
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods

  useEffect(() => {
    if (agencyAdditionalConfig) {
      const rawAgencyInterestRates =
        agencyAdditionalConfig?.agencyInterestRates ??
        agencyAdditionalConfig?.agency_interest_rates ??
        []

      const normalizedAgencyInterestRates = rawAgencyInterestRates
        ?.map(item => ({
          ...item,
          installment:
            item?.installment == null || item?.installment === '' ? null : Number(item.installment),
          interestRate:
            item?.interestRate == null || item?.interestRate === ''
              ? item?.interest_rate == null || item?.interest_rate === ''
                ? null
                : Number(item.interest_rate)
              : Number(item.interestRate),
        }))
        ?.slice()
        .sort((a, b) => {
          const ia = a.installment ?? Number.MAX_SAFE_INTEGER
          const ib = b.installment ?? Number.MAX_SAFE_INTEGER
          return ia - ib
        })

      reset({
        agencyInterestRates: normalizedAgencyInterestRates ?? [],
      })
    }
  }, [agencyAdditionalConfig, reset])

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true)

        const agencyInterestRates = (data?.agencyInterestRates ?? []).map(item => {
          if (!item) return item
          const payloadItem = { ...item }
          delete payloadItem.fieldId
          return payloadItem
        })

        const payload = {
          ...data,
          agencyInterestRates,
          maxInstallmentsAllowed: agencyInterestRates?.length ? agencyInterestRates.length + 1 : 1,
        }
        const payloadToSend = createFormData(payload)

        await dispatch(updateAgencyAdditionalConfig(agencyId, payloadToSend))
        showToast({ type: 'success', message: 'Configuração de email salva com sucesso' })
      } catch (error) {
        console.error(error)
        showToast({ type: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [agencyId, email?.id]
  )

  function handleReset() {
    if (agencyId) {
      getDataForm()
    }

    showToast({ type: 'success', message: 'Modificações canceladas' })
    return reset()
  }

  const goBack = () => {
    navigate(-1)
  }

  return (
    <FormProvider {...methods}>
      <form id="add-config" className={styles.app}>
        <HeaderTitle title="Configurações adicionais" backButtonAction={goBack}>
          <Grid style={{ display: 'flex', gap: '8px' }}>
            <Button className={[styles.buttonRed, styles.button]} onClick={() => handleReset()}>
              Cancelar
            </Button>
            <Button
              className={[styles.buttonFullBlue, styles.button]}
              form="add-config"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Salvar
            </Button>
          </Grid>
        </HeaderTitle>

        <Grid className={styles.container}>
          <Parcelamento />
          {errors?.agencyInterestRates && (
            <FormHelperText className={styles.errorMessage} error={!!errors?.agencyInterestRates}>
              Erro no formulário de parcelamento
            </FormHelperText>
          )}
        </Grid>
        <Loader hasBackdrop open={loading} label="Aguarde..." />
      </form>
    </FormProvider>
  )
}
